/*
 * Author: Dw
 * File Created: Friday, 24-09-13 02:41 pm
 * Last Modified: Saturday, 24-09-14 03:42 pm, by Harper Yan
 */

import React, { useRef, useState } from 'react';
import { i18n, useTranslation } from 'next-i18next';
import { motion } from 'framer-motion';
import { Box, Stack, Typography } from '@mui/material';

// 定义数据类型
interface TimelineItem {
  id: number;
  title: string;
  content: string;
}
const TimelineSwiper: React.FC = () => {
  const {
    t
  } = useTranslation('home');
  const [activeItem, setActiveItem] = useState<number>(0); // 状态类型为 number
  const containerRef = useRef<HTMLDivElement | null>(null); // 容器引用，类型为 HTMLDivElement 或 null

  const getCardInfo = (): Partial<{
    title: string;
    desc: string;
  }>[] => {
    return t('card.info', {
      returnObjects: true
    });
  };

  // 点击时处理滚动
  const handleItemClick = (id: number) => {
    setActiveItem(id);
    const itemElement = document.getElementById(`timeline-item-${id}`);
    const container = containerRef.current;
    if (container && itemElement) {
      const itemTop = itemElement.offsetTop;
      const itemHeight = itemElement.offsetHeight;
      const containerHeight = container.offsetHeight;
      const scrollPosition = itemTop - containerHeight / 2 + itemHeight / 2 + 50;
      smoothScroll(container, container.scrollTop, scrollPosition, 500);
    }
  };

  // 平滑滚动函数
  const smoothScroll = (element: HTMLElement, from: number, to: number, duration: number) => {
    const start = from;
    const change = to - start;
    const startTime = performance.now();
    const animateScroll = (currentTime: number) => {
      const timeElapsed = currentTime - startTime;
      const run = easeInOutQuad(timeElapsed, start, change, duration);
      element.scrollTop = run;
      if (timeElapsed < duration) {
        requestAnimationFrame(animateScroll);
      }
    };
    requestAnimationFrame(animateScroll);
  };

  // 缓动函数
  const easeInOutQuad = (tt: number, b: number, c: number, d: number): number => {
    // eslint-disable-next-line no-param-reassign -- ignore
    tt /= d / 2;
    if (tt < 1) return c / 2 * tt * tt + b;
    // eslint-disable-next-line no-param-reassign -- ignore
    tt--;
    return -c / 2 * (tt * (tt - 2) - 1) + b;
  };
  return <Box sx={{
    maxHeight: [300, 320],
    position: 'relative'
  }} data-sentry-element="Box" data-sentry-component="TimelineSwiper" data-sentry-source-file="TimeLine.tsx">
      <Box sx={{
      position: 'absolute',
      top: '-10px',
      width: '3px',
      left: '10px',
      bottom: '-10px',
      background: 'linear-gradient(180deg, rgba(108, 95, 255, 0.00) -1.56%, #6C5FFF 49.66%, rgba(108, 95, 255, 0.00) 101.46%)'
    }} data-sentry-element="Box" data-sentry-source-file="TimeLine.tsx" />

      {/* 主内容容器应用遮罩 */}
      <Stack ref={containerRef} sx={{
      overflowY: 'hidden',
      maxHeight: [320],
      px: '20px',
      py: [3],
      gap: [0, '20px'],
      position: 'relative',
      maskImage: 'linear-gradient(to bottom, transparent 0px, black 40px, black calc(100% - 40px), transparent 100%)',
      // 上下边缘60px渐隐效果
      maskSize: '100% 100%',
      pointerEvents: 'all',
      zIndex: 1
    }} data-sentry-element="Stack" data-sentry-source-file="TimeLine.tsx">
        {getCardInfo().map((item, index) => <Box animate={{
        scale: activeItem === index ? 1 : 0.9
      }} component={motion.div} id={`timeline-item-${index}`} initial={{
        opacity: 1,
        scale: 1
      }} key={index} onClick={() => {
        handleItemClick(index);
      }} sx={{
        position: 'relative',
        padding: '20px',
        px: [2.5, 4],
        borderRadius: '20px',
        // mb: ['0', '54px',],
        display: 'flex',
        flexDirection: 'column',
        // minHeight: '90px',
        transformOrigin: 'left center',
        zIndex: 1
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }}>
            <Typography animate={{
          color: activeItem === index ? '#FFFFFF' : '#707A8A'
        }} component={motion.h6} fontSize={['18px', '32px']} position="relative" sx={{
          '&:hover': {
            color: '#fff!important'
          },
          lineHeight: '120%'
        }} transition={{
          duration: 0.3
        }}>
              {item.title}
              {/* 圆标，独立 z-index，防止遮罩影响 */}
              <Box animate={{
            backgroundColor: activeItem === index ? '#A993FF' : '#1A1A1A',
            transform: 'translate(0, -50%)',
            outline: activeItem === index ? '2px solid #A993FF' : '2px solid #5a5085'
          }} component={motion.div} initial={{
            backgroundColor: '#1A1A1A',
            border: '3px solid #1A1A1A'
          }} sx={{
            position: 'absolute',
            outline: '2px solid #A993FF',
            left: ['-36px', '-48px'],
            top: '50%',
            width: '14px',
            height: '14px',
            cursor: 'pointer',
            zIndex: 10,
            // 设置足够高的 z-index，使其不受遮罩影响
            borderRadius: '50%'
          }} />
            </Typography>

            <Typography animate={{
          opacity: activeItem === index ? 1 : 0.3
        }} component={motion.p} initial={{
          opacity: 0.5
        }} sx={{
          color: '#848E9C',
          marginTop: '8px',
          fontWeight: 400,
          fontSize: ['14px', '18px'],
          lineHeight: ['18px', 'normal']
        }} transition={{
          duration: 0.3
        }}>
              {item.desc}
            </Typography>
          </Box>)}
      </Stack>
    </Box>;
};
export default TimelineSwiper;